import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import compareVersions, { VERSION_CM_12725_ASSET_AUTO_ARCHIVE, VERSION_CM_8339_FOLDER_VIEW } from "@cores/version";
import { IconButton, Tooltip, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconAllView, IconHierarchy } from "@mzc-cloudplex/icons";
import { TABLE_LIST_TYPE, GROUPING_VIEW_TYPE, LIST_VIEW_TYPE, UNCATEGORIZED_ID } from "@constants";
import { SearchQueryParamsContext, useSearchQueryParams } from "@components/params";
import { loadViewInfo, saveViewInfo } from "@cores/viewStorage";
import { useParams } from "react-router-dom";

const ListViewSelectButton = ({ domain, isActiveView, updateActiveView }) => {
    const { t } = useTranslation();
    const { schemaId } = useParams();

    const searchQueryParamsContext = useSearchQueryParams(SearchQueryParamsContext);
    const { stage, project } = useSelector(({ stage, project }) => ({
        stage,
        project,
    }));

    const onClickHierarchyViewType = async (type) => {
        const hierarchyType =
            compareVersions(stage.version, VERSION_CM_8339_FOLDER_VIEW) >= 0 &&
            [TABLE_LIST_TYPE.ASSET, TABLE_LIST_TYPE.COLLECTION, TABLE_LIST_TYPE.CUSTOM_CONTENT].includes(domain)
                ? "folder"
                : "category";

        const viewInfoKey =
            domain === TABLE_LIST_TYPE.CUSTOM_CONTENT ? `${schemaId}ViewInfo` : `${domain.toLowerCase()}sViewInfo`;

        let params = {
            activeView: type,
        };
        if (searchQueryParamsContext.params.hasOwnProperty("enableFolderView"))
            params.enableFolderView = searchQueryParamsContext.params.enableFolderView;
        if (searchQueryParamsContext.params.hasOwnProperty("enableCategoryView"))
            params.enableCategoryView = searchQueryParamsContext.params.enableCategoryView;
        if (
            compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0 &&
            domain === TABLE_LIST_TYPE.ASSET
        ) {
            params.withAutoArchiveDate = true;
        }
        if (searchQueryParamsContext.params.isNotIngestType) {
            params.isNotIngestType = searchQueryParamsContext.params.isNotIngestType;
        }
        if (searchQueryParamsContext.params.selectedContentSchemaId) {
            params.selectedContentSchemaId = searchQueryParamsContext.params.selectedContentSchemaId;
        }

        if (type.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY) {
            if (hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                let viewInfo = loadViewInfo(viewInfoKey, stage.id, project.id);
                let selectedCategoryId = viewInfo?.selectedCategoryId ?? UNCATEGORIZED_ID;
                params.hierarchyType = hierarchyType;
                params.selectedCategoryId = selectedCategoryId;
                params.isIncludeChildCategory = true;
            } else {
                params.hierarchyType = hierarchyType;
            }
        } else {
            saveViewInfo(viewInfoKey, {
                stageId: stage.id,
                projectId: project.id,
                activeView: "list",
            });
        }

        if (updateActiveView)
            updateActiveView({
                isActiveView: type,
                hierarchyType,
            });
            
        searchQueryParamsContext.resetSearchQueryParams({
            action: "push",
            params,
        });
    };

    return (
        <React.Fragment>
            {isActiveView === LIST_VIEW_TYPE.SEARCH ? (
                <Typography variant={"subtitle1_700"}>
                    {t(`common::label::Search results`, "Search results")}
                </Typography>
            ) : (
                <Tooltip
                    title={
                        isActiveView === LIST_VIEW_TYPE.LIST
                            ? `${t(`common::label::Grouping View mode`, "Grouping View mode")}`
                            : `${t(`common::label::View All mode`, "View All mode")}`
                    }
                >
                    <IconButton
                        size={"medium"}
                        variant={"contained"}
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            "&:hover, &:focus, &:active, .Mui-Selected": {
                                backgroundColor: theme.palette.primary.light,
                            },
                            marginRight: 1,
                        }}
                        onClick={(e) => {
                            isActiveView === LIST_VIEW_TYPE.LIST
                                ? onClickHierarchyViewType(LIST_VIEW_TYPE.HIERARCHY)
                                : onClickHierarchyViewType(LIST_VIEW_TYPE.LIST);
                        }}
                    >
                        {isActiveView === LIST_VIEW_TYPE.LIST ? (
                            <IconHierarchy size={16} htmlColor={"#fff"} />
                        ) : (
                            <IconAllView size={16} htmlColor={"#fff"} />
                        )}
                    </IconButton>
                </Tooltip>
            )}
        </React.Fragment>
    );
};

export default ListViewSelectButton;
